import React, { useEffect, useState, useRef } from 'react';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PasswordResetModal } from '../PasswordResetModal';
import { AlertCircleIcon, CheckCircleIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
export const StudentLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  // When we detect that the visitor is already authenticated (e.g. they
  // arrived on this page via a stale verification tab after already signing
  // in elsewhere), we render a minimal "Already signed in" state and
  // redirect to the appropriate dashboard. This avoids showing a duplicate
  // sign-in form to a user who already has an active session.
  const [alreadySignedIn, setAlreadySignedIn] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const formRef = useRef<HTMLFormElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const hasRedirectedRef = useRef(false);
  // Tracks whether the user submitted the sign-in form on this page. When
  // true, the "already signed in" detection effect should bail out — the
  // active submit handler owns the post-login redirect, so we must not
  // briefly render the "Already signed in" panel during the auth-state
  // transition.
  const didSubmitRef = useRef(false);
  const {
    login,
    currentUser,
    userType
  } = useAuth();
  // Check for verification success or error from URL params
  const isVerified = searchParams.get('verified') === 'true';
  const verificationError = searchParams.get('error') === 'verification_failed';
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
    localStorage.removeItem('scrollToTopAfterSignIn');
  }, []);
  // Detect already-authenticated visitors and route them to the correct
  // dashboard instead of showing a redundant sign-in form. This handles the
  // case where the verification tab auto-redirects here after another tab
  // has already completed sign-in.
  useEffect(() => {
    if (hasRedirectedRef.current) return;
    if (!currentUser) return;
    // Only redirect once we have a verified session — unverified users still
    // need to complete the verification flow before reaching the dashboard.
    if (!currentUser.emailVerified) return;
    // If the user is actively signing in via the form on this page, skip the
    // "Already signed in" UI entirely. handleSubmit owns the redirect and
    // showing the intermediate panel would cause a brief flash before the
    // dashboard mounts.
    if (didSubmitRef.current) return;
    hasRedirectedRef.current = true;
    setAlreadySignedIn(true);
    // Resolve destination from AuthContext userType (already resolved from
    // Firestore by AuthContext on session start). Fallback to localStorage
    // for parity with the rest of the verification flow. Default to the
    // student dashboard since this is the student login page.
    const resolvedType = userType || localStorage.getItem('userType') || 'student';
    const destination = resolvedType === 'teacher' ? '/teacher-dashboard' : '/student-dashboard';
    // Small delay so the success state renders cleanly before navigation.
    setTimeout(() => {
      navigate(destination, {
        replace: true
      });
    }, 600);
  }, [currentUser, userType, navigate]);
  // Scroll to error message when error state changes
  useEffect(() => {
    if (error) {
      window.scrollTo(0, 0);
      if (errorRef.current) {
        setTimeout(() => {
          errorRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }, 100);
      }
    }
  }, [error]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    // Mark the form as actively submitting so the already-signed-in
    // detection effect bails out while auth state transitions.
    didSubmitRef.current = true;
    setIsSubmitting(true);
    try {
      console.log('=== STUDENT LOGIN ATTEMPT ===');
      console.log('Email:', email);
      console.log('Calling login with type: student');
      // Use Firebase authentication
      await login(email, password, 'student');
      console.log('Login successful, navigating to dashboard');
      // Navigate to dashboard on successful login
      navigate('/student-dashboard');
    } catch (error: any) {
      console.error('=== STUDENT LOGIN ERROR ===');
      console.error('Login error:', error);
      console.error('Error message:', error.message);
      console.error('Error code:', error.code);
      // Handle user-actionable errors specifically
      if (error.code === 'auth/too-many-requests') {
        setError('Too many failed login attempts. Please try again later or reset your password.');
      } else if (error.code === 'auth/network-request-failed') {
        setError('Network error. Please check your internet connection and try again.');
      } else {
        // Generic message for all auth failures (security best practice)
        setError('Invalid email or password');
      }
      // Sign-in failed — clear the active-submit flag so a subsequent valid
      // session restore (e.g. via "Forgot password" flow) can still trigger
      // the already-signed-in detection.
      didSubmitRef.current = false;
    } finally {
      setIsSubmitting(false);
    }
  };
  // Render a clean "Already signed in" state instead of the sign-in form
  // when we've detected an existing authenticated, verified session. The
  // redirect to the appropriate dashboard is already scheduled by the
  // detection effect above.
  if (alreadySignedIn) {
    return <div className="w-full min-h-screen bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50] text-white font-sans" data-id="element-7460">
        <Navbar data-id="element-7461" />
        <main className="pt-32 pb-16" data-id="element-7462">
          <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7463">
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 p-8 text-center" data-id="element-7464">
              <div className="bg-green-50 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center" data-id="element-7465">
                <CheckCircleIcon className="h-8 w-8 text-green-500" data-id="element-7466" />
              </div>
              <h2 className="text-2xl font-serif font-bold text-navy-900 mb-2" data-id="element-7467">
                You're already signed in
              </h2>
              <p className="text-gray-600 mb-6" data-id="element-7468">
                Taking you to your dashboard…
              </p>
              <Link to={(userType || localStorage.getItem('userType')) === 'teacher' ? '/teacher-dashboard' : '/student-dashboard'} className="inline-block bg-deep-pink hover:bg-burgundy text-white font-medium py-3 px-6 rounded-lg transition-colors" data-id="element-7469">
                Continue to dashboard
              </Link>
            </div>
          </div>
        </main>
        <Footer data-id="element-7470" />
      </div>;
  }
  return <div className="w-full min-h-screen bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50] text-white font-sans" data-id="element-7471">
      <Navbar data-id="element-7472" />
      <main className="pt-32 pb-16" data-id="element-7473">
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7474">
          <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 p-8" data-id="element-7475">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-navy-900 mb-6 text-center" data-id="element-7476">
              Student Login
            </h2>
            <p className="text-gray-600 text-center mb-8" data-id="element-7477">
              Sign in to access your student dashboard
            </p>

            {/* Email verified success banner */}
            {isVerified && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-start mb-6" data-id="element-7478">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-7479" />
                <span data-id="element-7480">
                  Email verified successfully! Please sign in to continue.
                </span>
              </div>}

            {/* Verification error banner */}
            {verificationError && <div className="bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded-lg flex items-start mb-6" data-id="element-7481">
                <AlertCircleIcon className="h-5 w-5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-7482" />
                <span data-id="element-7483">
                  Email verification failed. Please try again or request a new
                  verification email.
                </span>
              </div>}

            {/* Error message at the top of the form */}
            {error && <div ref={errorRef} className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start mb-6" data-id="element-7484">
                <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-7485" />
                <span data-id="element-7486">{error}</span>
              </div>}
            <form ref={formRef} onSubmit={handleSubmit} data-id="element-7487">
              <div className="space-y-6" data-id="element-7488">
                {/* Email Address */}
                <div data-id="element-7489">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-7490">
                    Email Address
                  </label>
                  <input type="email" id="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 transition-colors text-gray-900" placeholder="you@example.com" required autoCapitalize="off" autoComplete="email" autoCorrect="off" spellCheck="false" inputMode="email" data-id="element-7491" />
                </div>
                {/* Password */}
                <div data-id="element-7492">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-7493">
                    Password
                  </label>
                  <input type="password" id="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 transition-colors text-gray-900" placeholder="Enter your password" required autoComplete="current-password" autoCapitalize="off" data-id="element-7494" />
                </div>
                {/* Forgot Password */}
                <div className="text-right" data-id="element-7495">
                  <button type="button" onClick={() => setIsResetModalOpen(true)} className="text-sm text-deep-pink hover:text-burgundy" data-id="element-7496">
                    Forgot your password?
                  </button>
                </div>
                {/* Submit Button */}
                <button type="submit" disabled={isSubmitting} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex justify-center items-center" data-id="element-7497">
                  {isSubmitting ? <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-7498">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-7499"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-7500"></path>
                      </svg>
                      Signing In...
                    </> : 'Sign In'}
                </button>
                <p className="text-sm text-gray-500 text-center mt-4" data-id="element-7501">
                  Don't have an account?{' '}
                  <Link to="/student-signup" className="text-blue-600 hover:text-blue-700" data-id="element-7502">
                    Create account
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </main>
      <Footer data-id="element-7503" />
      {/* Password Reset Modal */}
      <PasswordResetModal isOpen={isResetModalOpen} onClose={() => setIsResetModalOpen(false)} data-id="element-7504" />
    </div>;
};